import React from "react";
import { cn } from "@/lib/utils";
import { isBeveragePsdRecipe } from "../../application-recipes/data/ingredientsCalculations";

/**
 * Sample-scoped Batch Summary.
 *
 * This is intentionally a SEPARATE component from the recipe
 * `application-recipes/components/BatchSummary.jsx`. The recipe component reads
 * from and writes to the Recipe document; this one reads from and writes to
 * `Sample.batchSummary` only, so editing a sample can never mutate the recipe
 * it was copied from.
 */

const EMPTY_BATCH_SUMMARY = {
  outputYield: null,
  outputServingSize: null,
  perPiece: null,
  packetQuantity: null,
  servingSizeText: "",
  diluentName: "",
  diluentVolume: null,
  bffCostCalculation: null,
  clientCostCalculation: null,
};

function SummaryCard({ children, isDimmed = false, className = "" }) {
  return (
    <div
      className={cn(
        "border border-[#EEEBF4] dark:border-primary/30 rounded-[8px] overflow-hidden bg-[#FCFBFD] dark:bg-[#121019] text-xs divide-y divide-[#EEEBF4] dark:divide-primary/30 shadow-[0_1px_2px_rgba(0,0,0,0.02)] transition-opacity duration-200",
        isDimmed && "opacity-40 pointer-events-none select-none",
        className
      )}
    >
      {children}
    </div>
  );
}

function SummaryRow({ label, subLabel, value, unit, isDimmed = false, isEditing = false, editInput = null }) {
  return (
    <div
      className={cn(
        "grid grid-cols-2 divide-x divide-[#EEEBF4] dark:divide-primary/30 min-h-[44px] transition-opacity duration-200",
        isDimmed && "opacity-40 pointer-events-none select-none"
      )}
    >
      <div className="px-3.5 py-2.5 bg-[#F9FAFB] dark:bg-white/[0.02] flex flex-col justify-center">
        <span className="text-[13px] font-medium text-[#757575] dark:text-gray-400 leading-snug">
          {label}
        </span>
        {subLabel && (
          <span className="text-[11px] font-medium text-[#757575] dark:text-gray-400 leading-snug">
            {subLabel}
          </span>
        )}
      </div>

      <div className="px-3.5 py-2.5 bg-white dark:bg-[#0D0B14] flex items-center">
        {isEditing && editInput ? (
          editInput
        ) : (
          <span className="text-xs font-bold text-gray-900 dark:text-white flex items-center gap-1">
            <span>{value ?? "-"}</span>
            {unit && <span className="font-bold text-gray-900 dark:text-white">{unit}</span>}
          </span>
        )}
      </div>
    </div>
  );
}

function EditableCellInput({ value, onChange, unit, disabled = false }) {
  return (
    <div className="flex items-center gap-1.5 w-full">
      <input
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-20 font-bold text-xs text-gray-900 dark:text-white bg-transparent border-b border-[#4B208B] focus:border-b-2 focus:border-[#4B208B] focus:outline-none py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
      />
      {unit && (
        <span className="font-bold text-xs text-gray-900 dark:text-white">{unit}</span>
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="mb-3">
      <span className="font-bold text-sm text-gray-900 dark:text-white">{children}</span>
    </div>
  );
}

const formatNum = (num, fallback = "0") => {
  if (num === null || num === undefined || isNaN(num)) return fallback;
  const n = Number(num);
  return n % 1 === 0 ? String(n) : n.toFixed(2);
};

export default function SampleBatchSummary({
  value,
  onChange,
  recipeFormat = "bakery",
  batchSize = null,
  output = null,
  onSampleChange,
  disabled = false,
  isFromFinalRecipe = false,
  isBffCostEditable = true,
  showClientCost = false,
  showServingDetails = true,
}) {
  const canEditBffCost = !disabled && isBffCostEditable && !isFromFinalRecipe;
  const batchSummary = { ...EMPTY_BATCH_SUMMARY, ...(value || {}) };
  const costCalc = batchSummary.bffCostCalculation || {};

  const isBeveragePsd = isBeveragePsdRecipe(recipeFormat);
  const pieceLabel = isBeveragePsd ? "Per Sachet" : "Per Piece";
  const costPieceLabel = isBeveragePsd ? "Cost Per Sachet" : "Cost Per Piece";
  const pieceCostUnit = isBeveragePsd ? "৳/sachet" : "৳/pcs";

  // Total Quantity = batchSize
  const numBatchSize = Number(batchSize) || 0;
  const numYield = batchSummary.outputYield != null ? Number(batchSummary.outputYield) : 100;
  const numServingSize = Number(batchSummary.outputServingSize) || 0;

  // Output Block:
  // Output (g): Total Quantity * (Yield / 100)
  const calculatedOutputGrams = numBatchSize > 0 ? numBatchSize * (numYield / 100) : 0;
  // Output Pieces (pcs): Output / Serving Size
  const calculatedOutputPieces =
    numServingSize > 0 && calculatedOutputGrams > 0 ? calculatedOutputGrams / numServingSize : 0;

  // BFF Cost Calculation Block:
  // Total BFF Cost
  const totalBFFCost =
    Number(costCalc.totalBFFCost) ||
    Number(costCalc.batchSizeCost) ||
    (Number(costCalc.costPerPiece) > 0 && Number(costCalc.packetQuantity) > 0
      ? Number(costCalc.costPerPiece) * Number(costCalc.packetQuantity)
      : 0) ||
    (Number(costCalc.labOutputCost) > 0 ? Number(costCalc.labOutputCost) : 0) ||
    (Number(costCalc.wastageCost) > 0 ? Number(costCalc.wastageCost) : 0) ||
    0;

  const numPerPiece = batchSummary.perPiece != null ? Number(batchSummary.perPiece) : 0;
  const numPacketQuantity = batchSummary.packetQuantity != null ? Number(batchSummary.packetQuantity) : 0;

  // Cost Per Piece = Total BFF Cost / Packet Quantity
  const calculatedCostPerPiece =
    numPacketQuantity > 0 && totalBFFCost > 0
      ? totalBFFCost / numPacketQuantity
      : Number(costCalc.costPerPiece || 0);

  // Lab Output (g) = Per Piece * Packet Quantity
  const computedLabOutput =
    numPerPiece > 0 && numPacketQuantity > 0
      ? numPerPiece * numPacketQuantity
      : output != null
        ? Number(output)
        : Number(costCalc.labOutput || 0);

  const labOutput = output != null ? Number(output) : computedLabOutput;

  // Wastage (g) = Total Quantity - Lab Output
  const wastage = Math.max(0, numBatchSize - labOutput);

  // Lab Output Cost (BDT): if (Per Piece is 0 g) Display 0 else Total BFF Cost
  const labOutputCost = numPerPiece === 0 ? 0 : totalBFFCost;

  // Wastage Cost (BDT): if (Per Piece is 0 g) Total BFF Cost else Display 0
  const wastageCost = numPerPiece === 0 ? totalBFFCost : 0;

  const updateFields = (fields, sampleOverrides = {}) => {
    const nextSummary = { ...batchSummary, ...fields };
    const nextPerPiece =
      nextSummary.perPiece !== null && nextSummary.perPiece !== undefined
        ? Number(nextSummary.perPiece)
        : 0;
    const nextPacketQty =
      nextSummary.packetQuantity !== null && nextSummary.packetQuantity !== undefined
        ? Number(nextSummary.packetQuantity)
        : 0;
    const nextBatchSize =
      sampleOverrides.batchSize !== undefined
        ? Number(sampleOverrides.batchSize)
        : numBatchSize;

    const nextLabOutput =
      sampleOverrides.output !== undefined
        ? Number(sampleOverrides.output)
        : nextPerPiece > 0 && nextPacketQty > 0
          ? nextPerPiece * nextPacketQty
          : Number(output ?? computedLabOutput);

    const nextWastage = Math.max(0, nextBatchSize - nextLabOutput);
    const nextCostPerPiece =
      nextPacketQty > 0 && totalBFFCost > 0 ? totalBFFCost / nextPacketQty : 0;
    const nextLabOutputCost = nextPerPiece === 0 ? 0 : totalBFFCost;
    const nextWastageCost = nextPerPiece === 0 ? totalBFFCost : 0;

    const updatedBffCalc = {
      ...costCalc,
      totalBFFCost,
      perPiece: formatNum(nextPerPiece),
      packetQuantity: formatNum(nextPacketQty),
      costPerPiece: formatNum(nextCostPerPiece),
      labOutput: formatNum(nextLabOutput),
      labOutputCost: formatNum(nextLabOutputCost),
      labOutputCostUnit: costCalc.labOutputCostUnit || "৳",
      wastage: formatNum(nextWastage),
      wastageCost: formatNum(nextWastageCost),
    };

    onChange?.({
      ...nextSummary,
      bffCostCalculation: updatedBffCalc,
    });

    if (Object.keys(sampleOverrides).length > 0) {
      onSampleChange?.(sampleOverrides);
    }
  };

  return (
    <div className="space-y-5">
      <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/40 rounded-xl text-xs text-emerald-700 dark:text-emerald-300">
        These values were copied from the application recipe when this sample was
        created. Editing them changes this sample only &mdash; the recipe stays untouched.
      </div>

      {/* ================= Output ================= */}
      <div>
        <SectionTitle>Output</SectionTitle>
        <div className="space-y-3">
          <SummaryCard>
            <SummaryRow
              label="Yield"
              value={batchSummary.outputYield}
              unit="%"
              isEditing={!disabled}
              editInput={
                <EditableCellInput
                  value={batchSummary.outputYield}
                  onChange={(val) => updateFields({ outputYield: val === "" ? null : Number(val) })}
                  unit="%"
                  disabled={disabled}
                />
              }
            />
            <SummaryRow
              label="Serving Size"
              value={batchSummary.outputServingSize}
              unit="g"
              isEditing={!disabled}
              editInput={
                <EditableCellInput
                  value={batchSummary.outputServingSize}
                  onChange={(val) => updateFields({ outputServingSize: val === "" ? null : Number(val) })}
                  unit="g"
                  disabled={disabled}
                />
              }
            />
            <SummaryRow
              label="Batch Size"
              value={batchSize}
              unit="g"
              isEditing={!disabled}
              editInput={
                <EditableCellInput
                  value={batchSize}
                  onChange={(val) => updateFields({}, { batchSize: val === "" ? null : Number(val) })}
                  unit="g"
                  disabled={disabled}
                />
              }
            />
          </SummaryCard>

          <SummaryCard>
            <SummaryRow
              label="Output Pieces"
              value={calculatedOutputPieces > 0 ? formatNum(calculatedOutputPieces) : "-"}
              unit="pcs"
            />
            <SummaryRow
              label="Output"
              value={calculatedOutputGrams > 0 ? formatNum(calculatedOutputGrams) : "-"}
              unit="g"
            />
          </SummaryCard>
        </div>
      </div>

      {/* ================= BFF Cost Calculation ================= */}
      <div className="border-t border-[#EEEBF4] dark:border-primary/30 pt-5">
        <SectionTitle>BFF Cost Calculation</SectionTitle>
        <div className="space-y-3">
          <SummaryCard>
            <SummaryRow
              label={pieceLabel}
              value={batchSummary.perPiece}
              unit="g"
              isEditing={canEditBffCost}
              editInput={
                <EditableCellInput
                  value={batchSummary.perPiece}
                  onChange={(val) => {
                    const newP = val === "" ? null : Number(val);
                    const newOut = (Number(newP) || 0) * numPacketQuantity;
                    updateFields(
                      { perPiece: newP },
                      { perPackQuantity: newP, output: newOut }
                    );
                  }}
                  unit="g"
                  disabled={disabled}
                />
              }
            />
            <SummaryRow
              label={costPieceLabel}
              value={formatNum(calculatedCostPerPiece)}
              unit={pieceCostUnit}
            />
          </SummaryCard>

          <SummaryCard>
            <SummaryRow
              label="Packet Quantity"
              value={batchSummary.packetQuantity}
              unit="pcs"
              isEditing={canEditBffCost}
              editInput={
                <EditableCellInput
                  value={batchSummary.packetQuantity}
                  onChange={(val) => {
                    const newQ = val === "" ? null : Number(val);
                    const newOut = numPerPiece * (Number(newQ) || 0);
                    updateFields(
                      { packetQuantity: newQ },
                      { output: newOut }
                    );
                  }}
                  unit="pcs"
                  disabled={disabled}
                />
              }
            />
          </SummaryCard>

          <SummaryCard>
            <SummaryRow
              label="Lab Output"
              value={labOutput}
              unit="g"
              isEditing={!disabled}
              editInput={
                <EditableCellInput
                  value={labOutput}
                  onChange={(val) => updateFields({}, { output: val === "" ? null : Number(val) })}
                  unit="g"
                  disabled={disabled}
                />
              }
            />
            <SummaryRow
              label="Lab Output Cost"
              value={formatNum(labOutputCost)}
              unit={costCalc.labOutputCostUnit || "৳"}
            />
          </SummaryCard>

          <SummaryCard>
            <SummaryRow
              label="Wastage"
              value={formatNum(wastage)}
              unit="g"
            />
            <SummaryRow
              label="Wastage Cost"
              value={formatNum(wastageCost)}
              unit="৳"
            />
          </SummaryCard>
        </div>
      </div>

      {showClientCost && (
        <div className="border-t border-[#EEEBF4] dark:border-primary/30 pt-5">
          <SectionTitle>Client Cost Calculation</SectionTitle>
          {batchSummary.clientCostCalculation ? (
            <SummaryCard>
              <SummaryRow
                label="Cost per kg (without loss)"
                value={batchSummary.clientCostCalculation.costPerKgWithoutLoss ?? "-"}
                unit="৳/kg"
              />
              <SummaryRow
                label="Cost per kg (with loss)"
                value={batchSummary.clientCostCalculation.costPerKgWithLoss ?? "-"}
                unit="৳/kg"
              />
              <SummaryRow
                label={costPieceLabel}
                value={batchSummary.clientCostCalculation.costPerPiece ?? "-"}
                unit={pieceCostUnit}
              />
            </SummaryCard>
          ) : (
            <p className="text-sm text-muted-foreground">Client cost calculation is not available for this sample.</p>
          )}
        </div>
      )}

      {showServingDetails && (
        <div className="border-t border-[#EEEBF4] dark:border-primary/30 pt-5">
          <SectionTitle>Serving Size &amp; Diluent</SectionTitle>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                Serving Size Text
              </label>
              <input
                type="text"
                value={batchSummary.servingSizeText || ""}
                onChange={(e) => updateFields({ servingSizeText: e.target.value })}
                disabled={disabled}
                placeholder="e.g. 1 sachet of 12 g"
                className="w-full px-4 py-2.5 rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] border border-transparent focus:border-primary outline-none font-medium text-xs text-gray-900 dark:text-white disabled:opacity-60"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                  Diluent Name
                </label>
                <input
                  type="text"
                  value={batchSummary.diluentName || ""}
                  onChange={(e) => updateFields({ diluentName: e.target.value })}
                  disabled={disabled}
                  placeholder="e.g. Water"
                  className="w-full px-4 py-2.5 rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] border border-transparent focus:border-primary outline-none font-medium text-xs text-gray-900 dark:text-white disabled:opacity-60"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                  Diluent Volume (ml)
                </label>
                <input
                  type="number"
                  value={batchSummary.diluentVolume ?? ""}
                  onChange={(e) =>
                    updateFields({ diluentVolume: e.target.value === "" ? null : Number(e.target.value) })
                  }
                  disabled={disabled}
                  className="w-full px-4 py-2.5 rounded-xl bg-[#F0EDF5] dark:bg-[#1D1829] border border-transparent focus:border-primary outline-none font-bold text-xs text-gray-900 dark:text-white disabled:opacity-60"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
